import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { HierarchyNode } from '../HierarchyMappingPreview';
import {
  getIntegrationsApi,
  resolveIntegrationsWorkspaceId,
  type JiraProject,
} from '../../../../lib/integrationsApi';
import type { PriorityExportRecord, SyncStatus } from './integrationTypes';

type Notify = (notification: {
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
  duration?: number;
}) => void;

/** Jira connection, credentials, project data, and export (with hierarchy preview) handlers. */
export function useJiraConnection({
  api,
  workspaceId,
  listId,
  listName,
  priorities,
  onNotification,
  setSyncStatus,
}: {
  api: ReturnType<typeof getIntegrationsApi>;
  workspaceId: ReturnType<typeof resolveIntegrationsWorkspaceId>;
  listId: string;
  listName: string;
  priorities: PriorityExportRecord[];
  onNotification: Notify;
  setSyncStatus: Dispatch<SetStateAction<SyncStatus>>;
}) {
  // Connection state
  const [jiraConnected, setJiraConnected] = useState(false);
  const [showJiraConfig, setShowJiraConfig] = useState(false);

  const [jiraSiteUrl, setJiraSiteUrl] = useState(() => {
    try {
      return localStorage.getItem('peak_jira_site_url') || '';
    } catch {
      return '';
    }
  });
  const [jiraEmail, setJiraEmail] = useState(() => {
    try {
      return localStorage.getItem('peak_jira_email') || '';
    } catch {
      return '';
    }
  });
  const [jiraApiToken, setJiraApiToken] = useState(() => {
    try {
      return localStorage.getItem('peak_jira_token') || '';
    } catch {
      return '';
    }
  });

  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [selectedJiraProject, setSelectedJiraProject] = useState<string>('');

  // Modal states
  const [showExportJira, setShowExportJira] = useState(false);
  const [showHierarchyPreview, setShowHierarchyPreview] = useState(false);

  // Selection state
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);

  // Hierarchy options
  const [preserveHierarchy, setPreserveHierarchy] = useState(true);
  const [hierarchyItems, setHierarchyItems] = useState<HierarchyNode[]>([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // ==========================================================================
  // Jira Connection & Operations
  // ==========================================================================

  const handleJiraConnect = async () => {
    if (!jiraSiteUrl || !jiraEmail || !jiraApiToken) {
      onNotification({ type: 'error', title: 'Missing Fields', message: 'Please fill in all Jira connection fields' });
      return;
    }
    if (!workspaceId) {
      onNotification({
        type: 'error',
        title: 'Workspace not ready',
        message: 'Reload the page, then try connecting to Jira again.',
      });
      return;
    }

    setIsConnecting(true);
    try {
      const result = await api.connectJira(jiraSiteUrl, jiraEmail, jiraApiToken);
      if (result.connected) {
        setJiraConnected(true);
        setShowJiraConfig(false);
        
        // Persist credentials to localStorage
        try {
          localStorage.setItem('peak_jira_site_url', jiraSiteUrl);
          localStorage.setItem('peak_jira_email', jiraEmail);
          localStorage.setItem('peak_jira_token', jiraApiToken);
        } catch (_e) {
          console.warn('Could not persist Jira credentials to localStorage');
        }
        
        onNotification({ type: 'success', title: 'Connected', message: 'Successfully connected to Jira' });

        // Load projects
        const projects = await api.listJiraProjects();
        setJiraProjects(projects);
      } else {
        onNotification({ type: 'error', title: 'Connection Failed', message: result.error || 'Could not connect to Jira' });
      }
    } catch (error) {
      onNotification({ type: 'error', title: 'Error', message: error instanceof Error ? error.message : 'Connection failed' });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleJiraDisconnect = async () => {
    try {
      await api.disconnectJira();
      setJiraConnected(false);
      setJiraProjects([]);
      setSelectedJiraProject('');
      setJiraSiteUrl('');
      setJiraEmail('');
      setJiraApiToken('');
      
      // Clear from localStorage
      try {
        localStorage.removeItem('peak_jira_site_url');
        localStorage.removeItem('peak_jira_email');
        localStorage.removeItem('peak_jira_token');
      } catch (_e) {
        // ignore
      }
      
      onNotification({ type: 'info', title: 'Disconnected', message: 'Jira has been disconnected' });
    } catch (_error) {
      onNotification({ type: 'error', title: 'Error', message: 'Failed to disconnect from Jira' });
    }
  };

  // Build hierarchy from selected priorities
  const buildHierarchy = (): HierarchyNode[] => {
    const selected = priorities.filter(p => selectedPriorities.includes(p.id));

    if (!preserveHierarchy || selected.length <= 1) {
      return [{
        id: 'epic-1',
        title: listName,
        type: 'epic',
        sourceType: 'internal',
        isNew: true,
        children: selected.map(p => ({
          id: p.id,
          title: p.title,
          description: p.description != null ? String(p.description) : undefined,
          type: 'story' as const,
          sourceType: 'internal' as const,
          isNew: true,
          metadata: { votes: typeof p.score === 'number' ? Math.round(p.score * 10) : undefined },
        })),
      }];
    }

    // Group by category
    const byCategory = selected.reduce((acc, p) => {
      const cat = (p.category != null ? String(p.category) : '') || 'uncategorized';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(p);
      return acc;
    }, {} as Record<string, typeof selected>);

    return Object.entries(byCategory).map(([category, items], idx) => ({
      id: `epic-${idx}`,
      title: `${listName} - ${category}`,
      description: `Epic for ${category} priorities`,
      type: 'epic' as const,
      sourceType: 'internal' as const,
      isNew: true,
      children: items.map(p => ({
        id: p.id,
        title: p.title,
        description: p.description != null ? String(p.description) : undefined,
        type: 'story' as const,
        sourceType: 'internal' as const,
        isNew: true,
        metadata: { votes: typeof p.score === 'number' ? Math.round(p.score * 10) : undefined },
      })),
    }));
  };

  const handleJiraExportPreview = async () => {
    if (selectedPriorities.length === 0) return;

    const hierarchy = buildHierarchy();
    setHierarchyItems(hierarchy);
    setShowHierarchyPreview(true);
  };

  const confirmJiraExport = async () => {
    if (!selectedJiraProject || selectedPriorities.length === 0) {
      onNotification({ type: 'error', title: 'Missing Project', message: 'Please select a Jira project' });
      return;
    }

    setIsLoading(true);
    try {
      const exportPriorities = priorities.filter(p => selectedPriorities.includes(p.id));

      const result = await api.exportToJira(selectedJiraProject, exportPriorities, {
        includeDescription: true,
        includeRank: true,
        includeScore: true,
        sessionId: listId,
      });

      if (result.success) {
        onNotification({
          type: 'success',
          title: 'Exported to Jira',
          message: `Created ${result.summary.successful} issues in Jira`,
        });

        setSyncStatus(prev => ({
          ...prev,
          lastJiraSync: new Date(),
          linkedItems: prev.linkedItems + result.summary.successful,
        }));

        setShowExportJira(false);
        setShowHierarchyPreview(false);
        setSelectedPriorities([]);
      } else {
        onNotification({
          type: 'error',
          title: 'Export Failed',
          message: `Failed: ${result.failed.map(f => f.error).join(', ')}`,
        });
      }
    } catch (error) {
      onNotification({
        type: 'error',
        title: 'Export Error',
        message: error instanceof Error ? error.message : 'Failed to export to Jira',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const togglePrioritySelection = (id: string) => {
    setSelectedPriorities(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllPriorities = () => {
    setSelectedPriorities(priorities.map(p => p.id));
  };

  return {
    jiraConnected,
    setJiraConnected,
    showJiraConfig,
    setShowJiraConfig,
    jiraSiteUrl,
    setJiraSiteUrl,
    jiraEmail,
    setJiraEmail,
    jiraApiToken,
    setJiraApiToken,
    jiraProjects,
    setJiraProjects,
    selectedJiraProject,
    setSelectedJiraProject,
    showExportJira,
    setShowExportJira,
    showHierarchyPreview,
    setShowHierarchyPreview,
    selectedPriorities,
    setSelectedPriorities,
    preserveHierarchy,
    setPreserveHierarchy,
    hierarchyItems,
    setHierarchyItems,
    isLoading,
    isConnecting,
    handleJiraConnect,
    handleJiraDisconnect,
    handleJiraExportPreview,
    confirmJiraExport,
    togglePrioritySelection,
    selectAllPriorities,
  };
}

export type JiraConnection = ReturnType<typeof useJiraConnection>;
